import { AttachFile, DeleteForever } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Icon,
  Popper,
  Typography,
  Zoom
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import {
  Operations as DocumentAdminOperations,
  operations as documentAdminOperations
} from 'states/dux/document-admin/operations'
import { Payload as DocumentAdminPayload } from 'states/dux/document-admin/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFile } from 'types/nivos-file'
import { NivosFolder } from 'types/nivos-folder'
import { mapOperationsToDispatchProps, post } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
  documentAdmin: DocumentAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
    documentAdmin: DocumentAdminOperations
  }
}

interface OwnProps {
  fileDetails: NivosFile
  folderDetails: NivosFolder
  onClickEvent: (folder: NivosFile) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const DocumentAdminFileCardFC: React.FC<any> = (props: AppProps) => {
  const { fileDetails, folderDetails, theme, onClickEvent, operations } = props
  const [hovered, setHovered] = React.useState(false)
  const [deleteLoading, setLoading] = React.useState(false)
  const anchorElRef = React.useRef()

  const _deleteFile = (): void => {
    setLoading(true)
    void post(`/document/file/${fileDetails.id}/`, {}, false, false, 'DELETE')
      .then(() => {
        const newFolderState: NivosFolder = {
          ...folderDetails,
          files: folderDetails.files.filter(file => file.id !== fileDetails.id)
        }
        operations.documentAdmin.update(newFolderState)
        setLoading(false)
        setHovered(false)
      })
      .catch(err => {
        console.log(err)
        setLoading(false)
        setHovered(false)
      })
  }

  return (
    <>
      <Box
        ref={anchorElRef}
        height={'fit-content'}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onClickEvent(fileDetails)}
        sx={{
          cursor: 'pointer',
          margin: 2
        }}
      >
        <Icon
          sx={{
            fontSize: 30,
            transition: '0.5s',
            marginTop: hovered ? -1.5 : 0
          }}
        >
          <AttachFile fontSize={'inherit'} />
        </Icon>
        <Typography
          variant={'body1'}
          width={'85px'}
          align={'center'}
          noWrap
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {fileDetails.name}
        </Typography>
      </Box>
      {anchorElRef !== undefined && (
        <Popper
          sx={{ zIndex: 1301 }}
          open={hovered}
          anchorEl={anchorElRef.current}
          placement={'bottom'}
          transition
        >
          {({ TransitionProps }) => (
            <Zoom {...TransitionProps} timeout={350}>
              <Box
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                  backgroundColor: theme.primaryColor,
                  border: `1px solid ${theme.secondaryColor}`,
                  borderRadius: 4,
                  marginTop: -1.5
                }}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  width={300}
                >
                  <Box display={'flex'} flexDirection={'column'} padding={1}>
                    <Typography variant={'body2'}>
                      Filename: <strong>{fileDetails.name}</strong>
                    </Typography>
                    <Typography variant={'body2'}>
                      Created By:{' '}
                      <strong>{fileDetails.createdBy.fullName}</strong>
                    </Typography>
                    <Typography variant={'body2'}>
                      Created At:{' '}
                      <strong>{fileDetails.createdAt.toLocaleString()}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  borderTop={1}
                  onClick={() => {
                    if (!deleteLoading) {
                      _deleteFile()
                    }
                  }}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  {deleteLoading && (
                    <CircularProgress sx={{ height: 10, width: 10 }} />
                  )}
                  {!deleteLoading && (
                    <Icon>
                      <DeleteForever />
                    </Icon>
                  )}
                  <Typography variant={'body2'}>
                    {deleteLoading ? 'Loading...' : 'Delete File'}
                  </Typography>
                </Box>
              </Box>
            </Zoom>
          )}
        </Popper>
      )}
    </>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin,
    documentAdmin: state.documentAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations,
  documentAdmin: documentAdminOperations
})

const DocumentAdminFileCard = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentAdminFileCardFC) as React.FC<OwnProps>

export default DocumentAdminFileCard
